import React, { useEffect, useState } from "react";
import { Smartphone, Share, Plus, X, Check, Download } from "lucide-react";

function detectPlatform() {
  const ua = navigator.userAgent || navigator.vendor || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /android/i.test(ua);
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  return { isIOS, isAndroid, isStandalone };
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [platform, setPlatform] = useState({ isIOS: false, isAndroid: false, isStandalone: false });
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    setPlatform(detectPlatform());

    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };

    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const click = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === "accepted") setInstalled(true);
      setDeferred(null);
    } else if (platform.isIOS) {
      setShowIOSGuide(true);
    } else {
      // Generic fallback — show iOS-style guide that works for most browsers
      setShowIOSGuide(true);
    }
  };

  if (platform.isStandalone || installed) {
    return (
      <div className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[#0B5D3B]/5 border border-[#0B5D3B]/20 text-[#0B5D3B] text-sm" data-testid="install-success">
        <Check className="w-4 h-4" /> Installed — open from your home screen
      </div>
    );
  }

  return (
    <>
      <button
        onClick={click}
        className="inline-flex items-center gap-3 bg-[#111111] text-white px-5 py-3 rounded-2xl hover:bg-[#0B5D3B] transition-colors group"
        data-testid="install-app-btn"
      >
        <Download className="w-6 h-6 text-[#D4AF37] group-hover:scale-110 transition-transform" strokeWidth={1.8} />
        <div className="text-left leading-tight">
          <div className="text-[10px] uppercase tracking-widest text-white/70">
            {platform.isIOS ? "Install on iPhone" : platform.isAndroid ? "Install on Android" : "Install the app"}
          </div>
          <div className="font-serif text-lg">Add to Home Screen</div>
        </div>
      </button>

      {showIOSGuide && (
        <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="install-modal">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full relative">
            <button onClick={() => setShowIOSGuide(false)} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#FAF7EE] hover:bg-[#EAE3D2] flex items-center justify-center" data-testid="install-modal-close">
              <X className="w-4 h-4" />
            </button>
            <Smartphone className="w-10 h-10 text-[#0B5D3B] mb-4" strokeWidth={1.5} />
            <h3 className="font-serif text-2xl text-[#111] mb-2">Add ShoNics to your Home Screen</h3>
            <p className="text-sm text-[#4A554E] mb-6">Works on any phone — iPhone, iPad, Android, even desktop. Once installed, ShoNics opens just like a native app.</p>

            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#0B5D3B] text-white text-xs font-semibold flex items-center justify-center shrink-0">1</div>
                <div className="text-sm text-[#111] pt-1">
                  Tap the <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FAF7EE] rounded-md mx-1"><Share className="w-3.5 h-3.5" /> Share</span> button in your browser toolbar.
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#0B5D3B] text-white text-xs font-semibold flex items-center justify-center shrink-0">2</div>
                <div className="text-sm text-[#111] pt-1">
                  Scroll and choose <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FAF7EE] rounded-md mx-1"><Plus className="w-3.5 h-3.5" /> Add to Home Screen</span>.
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#D4AF37] text-[#0B5D3B] text-xs font-semibold flex items-center justify-center shrink-0">3</div>
                <div className="text-sm text-[#111] pt-1">
                  Tap <span className="font-semibold">Add</span>. The ShoNics icon will appear on your home screen.
                </div>
              </div>
            </div>

            <div className="mt-7 p-4 rounded-2xl bg-[#FAF7EE] text-xs text-[#4A554E]">
              Tip: on Android Chrome, you'll usually see a one-tap <span className="font-semibold">Install app</span> button in the menu instead.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
